<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  debounce,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import { getExtendedMessageVariables } from 'shared/helpers/messageVariablesHelper';
import { useAlert } from 'dashboard/composables';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import OperatingProcedureRunsAPI from 'dashboard/api/operatingProcedureRuns';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';
import { useSopRunsStore } from 'dashboard/stores/sopRuns';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import SopWalkthroughSummary from './SopWalkthroughSummary.vue';
import SimulatorNodePreview from 'dashboard/routes/dashboard/settings/sops/builder/SimulatorNodePreview.vue';
import { useSopSimulator } from 'dashboard/routes/dashboard/settings/sops/composables/useSopSimulator';
import { useSopSummary } from 'dashboard/routes/dashboard/settings/sops/composables/useSopSummary';
import {
  AUTOMATION_MODES,
  NODE_TYPES,
} from 'dashboard/routes/dashboard/settings/sops/helpers/sopFlow';

const props = defineProps({
  sop: { type: Object, default: null },
  conversationId: { type: [Number, String], required: true },
  // SOPs (id + name) whose reference node led the agent here.
  referencedBy: { type: Array, default: () => [] },
});

const emit = defineEmits(['openSop']);

const { t } = useI18n();
const store = useStore();
const sopStore = useOperatingProceduresStore();
const runsStore = useSopRunsStore();
const cannedResponses = useMapGetter('getCannedResponses');
const currentUser = useMapGetter('getCurrentUser');

// Variable resolution mirrors the reply box: automatic variables come from
// the conversation/contact/inbox context; anything else in the canned
// content is custom and gets typed in by the agent.
const currentChat = useMapGetter('getSelectedChat');
const contact = useFunctionGetter(
  'contacts/getContact',
  computed(() => currentChat.value?.meta?.sender?.id)
);
const inbox = useFunctionGetter(
  'inboxes/getInbox',
  computed(() => currentChat.value?.inbox_id)
);
const messageVariables = computed(() =>
  getExtendedMessageVariables({
    // getMessageVariables destructures conversation.meta unguarded.
    conversation: { meta: {}, ...(currentChat.value || {}) },
    contact: contact.value,
    inbox: inbox.value,
  })
);

const nodes = ref([]);
const edges = ref([]);
const simulator = useSopSimulator(nodes, edges);
const { summarySteps } = useSopSummary(
  nodes,
  edges,
  simulator,
  cannedResponses,
  messageVariables
);

// Each run persists per (conversation, SOP) so progress — path taken,
// checklist ticks, executed automations, agent feedback — survives reloads
// and is attributable to the agent.
const runId = ref(null);
const runExecutor = ref(null);
const isRunLoaded = ref(false);
let lastSavedRunData = null;

// The run row is created lazily: merely opening the panel (start node only,
// untouched inputs) must not record a run against the agent.
const hasProgress = () =>
  simulator.state.value.path.length > 1 ||
  Object.values(simulator.runData.value).some(
    node =>
      node.feedbacks?.length ||
      node.confirmed ||
      Object.values(node.checked).some(Boolean) ||
      Object.values(node.variables).some(Boolean)
  );

const persistRun = async () => {
  if (!isRunLoaded.value || !props.sop || !nodes.value.length) return;
  if (!runId.value && !hasProgress()) return;
  const runData = {
    path: simulator.state.value.path,
    nodes: simulator.runData.value,
  };
  const serialized = JSON.stringify(runData);
  if (serialized === lastSavedRunData) return;
  const payload = {
    operating_procedure_id: props.sop.id,
    conversation_id: props.conversationId,
    status: simulator.isComplete.value ? 'completed' : 'in_progress',
    run_data: runData,
  };
  try {
    const { data } = runId.value
      ? await OperatingProcedureRunsAPI.update(runId.value, payload)
      : await OperatingProcedureRunsAPI.create(payload);
    runId.value = data.id;
    runExecutor.value = data.user;
    // Completion is only shared (and unlocks referencing SOPs) once saved.
    runsStore.setStatus(props.conversationId, props.sop.id, data.status);
    // The server is the source of truth for feedback, including other agents'.
    Object.entries(data.run_data.nodes).forEach(([nodeId, node]) => {
      simulator.runDataFor(nodeId).feedbacks = node.feedbacks;
    });
    // Mark only what was sent as saved; changes made while in flight go next.
    lastSavedRunData = serialized;
  } catch (error) {
    // Silent: progress persistence is best-effort; next change retries.
  }
};
const debouncedPersistRun = debounce(persistRun, 800);

// Reload the walkthrough whenever the matched SOP or conversation changes:
// resume the persisted run when one exists, otherwise start fresh.
// `sop` is a live Pinia-store record (reactive proxy), so structuredClone
// can't touch its arrays directly — go through JSON, which this data
// already round-trips through as plain flow JSON from the API.
watch(
  () => [props.sop?.id, props.conversationId],
  async () => {
    isRunLoaded.value = false;
    runId.value = null;
    runExecutor.value = null;
    lastSavedRunData = null;
    nodes.value = JSON.parse(JSON.stringify(props.sop?.flow?.nodes || []));
    edges.value = JSON.parse(JSON.stringify(props.sop?.flow?.edges || []));
    if (!nodes.value.length) return;
    try {
      const { data } = await OperatingProcedureRunsAPI.forConversation({
        conversationId: props.conversationId,
        operatingProcedureId: props.sop.id,
      });
      const run = data.payload?.[0];
      if (run) {
        runId.value = run.id;
        runExecutor.value = run.user;
        runsStore.setStatus(props.conversationId, props.sop.id, run.status);
      }
      // A run keeps the flow it started on, so later SOP edits don't change it.
      if (run?.run_data?.flow) {
        nodes.value = run.run_data.flow.nodes;
        edges.value = run.run_data.flow.edges;
      }
      if (!run || !simulator.hydrate(run.run_data)) simulator.start();
      else
        lastSavedRunData = JSON.stringify({
          path: simulator.state.value.path,
          nodes: simulator.runData.value,
        });
    } catch (error) {
      simulator.start();
    }
    isRunLoaded.value = true;
  },
  { immediate: true }
);

// Persist on any progress: advancing, ticking checklist items, confirming
// automations, submitting feedback.
watch(
  () => [simulator.state.value.path.length, simulator.runData.value],
  () => debouncedPersistRun(),
  { deep: true }
);

const currentNode = computed(() => simulator.currentNode.value);
const nodeData = computed(() => currentNode.value?.data || {});

// Share the SOPs this run's path has reached through reference nodes.
// Referenced SOPs are shown as their own tabs; stepping back past a
// reference node drops the tab again (the referenced run is kept).
const reachedReferenceIds = computed(() => [
  ...new Set(
    simulator.state.value.path
      .map(nodeId => nodes.value.find(node => node.id === nodeId))
      .filter(node => node?.type === NODE_TYPES.REFERENCE)
      .map(node => node.data?.operating_procedure_id)
      .filter(Boolean)
  ),
]);

watch(
  [isRunLoaded, reachedReferenceIds],
  ([loaded, ids]) => {
    if (!loaded || !props.sop) return;
    runsStore.setReferences(props.conversationId, props.sop.id, ids);
  },
  { immediate: true }
);

onUnmounted(() => {
  if (props.sop) runsStore.clearReferences(props.conversationId, props.sop.id);
});

// A reference node holds the agent here until the referenced SOP is complete.
const isReferenceNode = computed(
  () => currentNode.value?.type === NODE_TYPES.REFERENCE
);
const referencedSop = computed(() =>
  sopStore.getById(nodeData.value.operating_procedure_id)
);
const isReferenceDone = computed(() =>
  runsStore.isCompleted(
    props.conversationId,
    nodeData.value.operating_procedure_id
  )
);
// Feedback is per agent and final once submitted. Drafts stay local until then.
const feedbackDrafts = ref({});
const draftFor = nodeId => feedbackDrafts.value[nodeId] || '';
const ownFeedbackFor = nodeId =>
  simulator.runData.value[nodeId]?.feedbacks?.find(
    feedback => feedback.user_id === currentUser.value.id
  );

const submitFeedback = nodeId => {
  const run = simulator.runDataFor(nodeId);
  run.feedbacks = [
    ...(run.feedbacks || []),
    {
      user_id: currentUser.value.id,
      name: currentUser.value.name,
      text: draftFor(nodeId).trim(),
    },
  ];
  feedbackDrafts.value[nodeId] = '';
  persistRun();
};

const cannedContentFor = node => {
  const automation = node?.data?.automation;
  if (automation?.type !== 'canned_response') return null;
  return (
    cannedResponses.value.find(item => item.id === Number(automation.id))
      ?.content || null
  );
};

// Custom {{variables}} in the current node's canned response that automatic
// injection can't resolve — the agent fills these in before confirming.
const customVariables = computed(() => {
  const content = cannedContentFor(currentNode.value);
  if (!content) return [];
  return getUndefinedVariablesInMessage({
    message: content,
    variables: messageVariables.value,
  });
});

// Unlike the builder's simulation, operating mode runs automations for
// real: macros execute on the conversation, canned responses drop into the
// reply editor. Automation rules fire on their own server-side triggers, so
// confirming one is just the agent acknowledging it.
const confirming = ref(false);

const executeAutomation = async node => {
  const automation = node.data?.automation;
  const run = simulator.runDataFor(node.id);
  confirming.value = true;
  try {
    if (automation?.type === 'macro') {
      await store.dispatch('macros/execute', {
        macroId: automation.id,
        conversationIds: [props.conversationId],
      });
      useAlert(t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
    } else if (automation?.type === 'canned_response') {
      const content = cannedContentFor(node);
      if (content) {
        // Automatic variables inject from context; agent-typed custom values
        // are added under both the exact and lowercased name because
        // replaceVariablesInMessage looks values up lowercased.
        const variables = { ...messageVariables.value };
        Object.entries(run.variables || {}).forEach(([key, value]) => {
          variables[key] = value;
          variables[key.toLowerCase()] = value;
        });
        emitter.emit(
          BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR,
          replaceVariablesInMessage({ message: content, variables })
        );
      }
    }
    run.confirmed = true;
  } catch (error) {
    useAlert(t('MACROS.ERROR'));
  } finally {
    confirming.value = false;
  }
};

// Auto-pilot automation nodes execute as soon as the walkthrough reaches
// them; manual ones wait for the agent's confirm click. Auto-pilot canned
// responses with custom variables also wait — the agent must fill the
// values in first.
watch(currentNode, node => {
  if (!node || node.type !== NODE_TYPES.AUTOMATION) return;
  if (
    (node.data?.mode || AUTOMATION_MODES.MANUAL) !== AUTOMATION_MODES.AUTO_PILOT
  )
    return;
  if (simulator.runDataFor(node.id).confirmed) return;
  if (customVariables.value.length) return;
  executeAutomation(node);
});
</script>

<template>
  <div
    class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-3"
  >
    <div class="flex items-start justify-between gap-2">
      <div class="flex min-w-0 items-start gap-1.5">
        <span
          class="i-lucide-workflow size-4 shrink-0 text-n-slate-11 mt-0.5"
        />
        <div class="min-w-0">
          <p
            class="mb-0 whitespace-pre-wrap break-words text-sm font-medium text-n-slate-12"
          >
            {{ sop?.name }}
          </p>
          <p
            v-if="referencedBy.length"
            class="mb-0 mt-0.5 flex flex-wrap items-center gap-1 text-xs text-n-slate-11"
          >
            {{ t('SOPS.SIMULATOR.REFERENCED_FROM') }}
            <Button
              v-for="referrer in referencedBy"
              :key="referrer.id"
              :label="referrer.name"
              icon="i-lucide-corner-up-left"
              xs
              slate
              faded
              @click="emit('openSop', referrer.id)"
            />
          </p>
          <p
            v-if="runExecutor"
            class="mb-0 mt-0.5 flex items-center gap-1.5 text-xs text-n-slate-11"
          >
            {{ t('SOPS.SIMULATOR.EXECUTED_BY') }}
            <Avatar
              :name="runExecutor.name"
              :src="runExecutor.avatar_url"
              :size="16"
              rounded-full
            />
            <span class="font-medium text-n-slate-12">
              {{ runExecutor.name }}
            </span>
          </p>
        </div>
      </div>
      <Button
        v-if="
          isRunLoaded &&
          simulator.state.value.path.length > 1 &&
          !simulator.isComplete.value
        "
        :label="t('SOPS.SIMULATOR.BACK')"
        icon="i-lucide-arrow-left"
        xs
        slate
        ghost
        class="shrink-0"
        @click="simulator.back()"
      />
    </div>

    <SimulatorNodePreview
      v-if="isRunLoaded && currentNode"
      :node="currentNode"
      :show-branches="false"
      :show-feedback="false"
      :run-data="simulator.runDataFor(currentNode.id)"
      operating
      :confirming="confirming"
      :message-variables="messageVariables"
      @confirm="executeAutomation"
    />

    <div
      v-if="isRunLoaded && isReferenceNode"
      class="flex flex-col gap-2 rounded-lg border p-3"
      :class="
        isReferenceDone
          ? 'border-n-teal-6 bg-n-teal-3'
          : 'border-n-amber-6 bg-n-amber-3'
      "
    >
      <p
        class="mb-0 flex items-center gap-1.5 text-sm font-medium"
        :class="isReferenceDone ? 'text-n-teal-11' : 'text-n-amber-11'"
      >
        <span
          :class="
            isReferenceDone
              ? 'i-lucide-circle-check'
              : 'i-lucide-corner-down-right'
          "
          class="size-4 shrink-0"
        />
        {{ t('SOPS.SIMULATOR.REFERENCE_TITLE') }}
      </p>
      <p class="mb-0 break-words text-sm text-n-slate-12">
        {{
          t(
            isReferenceDone
              ? 'SOPS.SIMULATOR.REFERENCE_DONE'
              : 'SOPS.SIMULATOR.REFERENCE_PENDING',
            { name: referencedSop?.name || '' }
          )
        }}
      </p>
      <Button
        v-if="referencedSop && !isReferenceDone"
        :label="
          t('SOPS.SIMULATOR.OPEN_REFERENCE', { name: referencedSop.name })
        "
        icon="i-lucide-arrow-right"
        trailing-icon
        sm
        teal
        faded
        class="self-start"
        @click="emit('openSop', referencedSop.id)"
      />
    </div>

    <template v-if="isRunLoaded && simulator.isComplete.value">
      <p class="mb-0 flex items-center gap-1.5 text-sm text-n-teal-11">
        <span class="i-lucide-circle-check size-4" />
        {{ t('SOPS.SIMULATOR.COMPLETE') }}
      </p>
    </template>

    <template v-else-if="isRunLoaded && simulator.isBranching.value">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('SOPS.SIMULATOR.PICK_BRANCH') }}
      </p>
      <div class="flex flex-col gap-1.5">
        <Button
          v-for="branch in nodeData.branches"
          :key="branch.id"
          :disabled="!simulator.hasEdgeFor(branch.id)"
          sm
          slate
          faded
          justify="start"
          class="!h-auto whitespace-normal py-1.5 text-left"
          @click="simulator.advance(branch.id)"
        >
          <span class="min-w-0 whitespace-pre-wrap break-words">
            {{ branch.label || t('SOPS.BUILDER.PANEL.BRANCH_PLACEHOLDER') }}
          </span>
        </Button>
      </div>
    </template>

    <template v-else-if="isRunLoaded">
      <p
        v-if="simulator.isDeadEnd.value"
        class="mb-0 flex items-center gap-1.5 text-sm text-n-amber-11"
      >
        <span class="i-lucide-triangle-alert size-4" />
        {{ t('SOPS.SIMULATOR.DEAD_END') }}
      </p>
      <Button
        v-else
        :label="t('SOPS.SIMULATOR.NEXT')"
        trailing-icon
        icon="i-lucide-arrow-right"
        sm
        :disabled="isReferenceNode && !isReferenceDone"
        @click="simulator.advance()"
      />
    </template>

    <div
      v-if="isRunLoaded && currentNode"
      class="border-t border-n-strong pt-3"
    >
      <template v-if="ownFeedbackFor(currentNode.id)">
        <TextArea
          :model-value="ownFeedbackFor(currentNode.id).text"
          class="!h-auto shrink-0"
          :label="t('SOPS.SIMULATOR.FEEDBACK_LABEL')"
          disabled
          auto-height
          min-height="5.25rem"
          max-height="none"
        />
        <p class="mb-0 mt-2 flex items-center gap-1.5 text-sm text-n-slate-11">
          {{ t('SOPS.SIMULATOR.FEEDBACK_SUBMITTED_BY') }}
          <Avatar
            :name="currentUser.name"
            :src="currentUser.avatar_url"
            :size="16"
            rounded-full
          />
          <span class="font-medium text-n-slate-12">{{
            currentUser.name
          }}</span>
        </p>
      </template>
      <template v-else>
        <TextArea
          :model-value="draftFor(currentNode.id)"
          class="!h-auto shrink-0"
          :label="t('SOPS.SIMULATOR.FEEDBACK_LABEL')"
          :placeholder="t('SOPS.SIMULATOR.FEEDBACK_PLACEHOLDER')"
          auto-height
          min-height="5.25rem"
          max-height="none"
          @update:model-value="feedbackDrafts[currentNode.id] = $event"
        />
        <Button
          :label="t('SOPS.SIMULATOR.FEEDBACK_SUBMIT')"
          :disabled="!draftFor(currentNode.id).trim()"
          sm
          class="mt-2"
          @click="submitFeedback(currentNode.id)"
        />
      </template>
    </div>

    <SopWalkthroughSummary
      v-if="isRunLoaded && simulator.isComplete.value && summarySteps.length"
      :steps="summarySteps"
    />
  </div>
</template>
